import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  CardActionArea,
  Button,
  Skeleton,
  useTheme,
  alpha,
  IconButton,
} from '@mui/material';
import { 
  People as PeopleIcon,
  Public as RegionsIcon,
  Business as ProducerIcon,
  Grain as GrapeIcon,
  LocalBar as WineIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import userService from '../../services/userService';
import wineRegionService from '../../services/wineRegionService';
import grapeService from '../../services/grapeService';
import producerService from '../../services/producerService';

const AdminDashboard: React.FC = () => {
  const { user, isSystemAdmin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [adminUserCount, setAdminUserCount] = useState<number>(0);
  const [regionCount, setRegionCount] = useState<number>(0);
  const [grapeCount, setGrapeCount] = useState<number>(0);
  const [producerCount, setProducerCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingRegions, setLoadingRegions] = useState<boolean>(true);
  const [loadingGrapes, setLoadingGrapes] = useState<boolean>(true);
  const [loadingProducers, setLoadingProducers] = useState<boolean>(true);

  // Fetch admin user count on component mount
  useEffect(() => {
    // Use a ref to track if the effect has run
    const controller = new AbortController();
    
    const fetchAdminUserCount = async () => {
      if (isSystemAdmin() && loading) {
        try {
          // Pass the signal to the fetch request
          const response = await userService.getAllUsers(1, 1); // Only need pagination data, not actual users
          if (!controller.signal.aborted) {
            // Use totalItems from pagination metadata
            setAdminUserCount(response.pagination.totalItems);
            setLoading(false);
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Failed to fetch admin users:', error);
            setLoading(false);
          }
        }
      }
    };

    fetchAdminUserCount();
    
    // Cleanup function to abort any in-flight requests
    return () => {
      controller.abort();
    };
  }, [isSystemAdmin, loading]);
  
  // Fetch wine regions count
  useEffect(() => {
    const controller = new AbortController();
    
    const fetchRegionCount = async () => {
      if (loadingRegions) {
        try {
          // Get all regions with minimal page size to get total count
          const response = await wineRegionService.getAllRegions(1, 1);
          if (!controller.signal.aborted) {
            setRegionCount(response.pagination.totalItems);
            setLoadingRegions(false);
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Failed to fetch wine regions:', error);
            setLoadingRegions(false);
          }
        }
      }
    };

    fetchRegionCount();
    
    return () => {
      controller.abort();
    };
  }, [loadingRegions]);
  
  // Fetch grape varieties count
  useEffect(() => {
    const controller = new AbortController();
    
    const fetchGrapeCount = async () => {
      if (loadingGrapes) {
        try {
          // Get all grapes with minimal page size to get total count
          const response = await grapeService.getAllGrapes(1, 1);
          if (!controller.signal.aborted) {
            setGrapeCount(response.pagination.totalItems);
            setLoadingGrapes(false);
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Failed to fetch grape varieties:', error);
            setLoadingGrapes(false);
          }
        }
      }
    };

    fetchGrapeCount();
    
    return () => {
      controller.abort();
    };
  }, [loadingGrapes]);
  
  // Fetch producers count
  useEffect(() => {
    const controller = new AbortController();
    
    const fetchProducerCount = async () => {
      if (loadingProducers) {
        try {
          // Get all producers with minimal page size to get total count
          const response = await producerService.getAllProducers(1, 1);
          if (!controller.signal.aborted) {
            setProducerCount(response.pagination.totalItems);
            setLoadingProducers(false);
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Failed to fetch producers:', error);
            setLoadingProducers(false);
          }
        }
      }
    };

    fetchProducerCount();
    
    return () => {
      controller.abort();
    };
  }, [loadingProducers]);
  
  // Define the dashboard cards
  const dashboardCards = [
    {
      title: 'Wines',
      subtitle: 'Total wines in database',
      count: 0,
      loading: false,
      icon: WineIcon,
      color: theme.palette.secondary.main,
      path: '/admin/wines',
      gradient: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
      available: false, // Not implemented yet
    },
    {
      title: 'Producers',
      subtitle: 'Wine producers worldwide',
      count: producerCount,
      loading: loadingProducers,
      icon: ProducerIcon,
      color: theme.palette.primary.main,
      path: '/admin/producers',
      gradient: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
      available: true,
    },
    {
      title: 'Regions',
      subtitle: 'Wine regions and appellations',
      count: regionCount,
      loading: loadingRegions,
      icon: RegionsIcon,
      color: theme.palette.tertiary.main,
      path: '/admin/regions',
      gradient: `linear-gradient(135deg, ${alpha(theme.palette.tertiary.main, 0.1)} 0%, ${alpha(theme.palette.tertiary.main, 0.05)} 100%)`,
      available: true,
    },
    {
      title: 'Grape Varieties',
      subtitle: 'Different grape varietals',
      count: grapeCount,
      loading: loadingGrapes,
      icon: GrapeIcon,
      color: '#34C759', // Apple green
      path: '/admin/grapes',
      gradient: `linear-gradient(135deg, ${alpha('#34C759', 0.1)} 0%, ${alpha('#34C759', 0.05)} 100%)`,
      available: true,
    },
  ];

  if (isSystemAdmin()) {
    dashboardCards.push({
      title: 'Admin Users',
      subtitle: 'System administrators',
      count: adminUserCount,
      loading: loading,
      icon: PeopleIcon,
      color: '#FF9500', // Apple orange
      path: '/admin/users',
      gradient: `linear-gradient(135deg, ${alpha('#FF9500', 0.1)} 0%, ${alpha('#FF9500', 0.05)} 100%)`,
      available: true,
    });
  }

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 },
      maxWidth: '1400px',
      mx: 'auto',
    }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 5 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 1,
          }}
        >
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.firstName || 'Admin'}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: theme.palette.text.secondary,
            fontSize: '1.1rem',
          }}
        >
          Manage your wine database with precision and elegance
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
          gap: 3 
        }}
      >
        {dashboardCards.map((card, index) => (
          <Card
            key={card.title}
            sx={{
              height: '200px',
              background: card.gradient,
              border: `1px solid ${alpha(card.color, 0.2)}`,
              position: 'relative',
              overflow: 'hidden',
              cursor: card.available ? 'pointer' : 'default',
              opacity: card.available ? 1 : 0.6,
              '&:hover': card.available ? {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 32px ${alpha(card.color, 0.15)}`,
              } : {},
            }}
          >
            <CardActionArea
              onClick={card.available ? () => navigate(card.path) : undefined}
              disabled={!card.available}
              sx={{ height: '100%', p: 0 }}
            >
              <CardContent sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: 3,
                '&:last-child': { pb: 3 },
              }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                      }}
                    >
                      {card.subtitle}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: alpha(card.color, 0.15),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <card.icon sx={{ color: card.color, fontSize: 24 }} />
                  </Box>
                </Box>

                {/* Count */}
                <Box>
                  {card.loading ? (
                    <Skeleton variant="text" width={120} height={60} />
                  ) : (
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        lineHeight: 1,
                        mb: 1,
                      }}
                    >
                      {card.count.toLocaleString()}
                    </Typography>
                  )}
                  
                  {card.available && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: card.color,
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      >
                        Manage {card.title.toLowerCase()}
                      </Typography>
                      <ArrowForwardIcon sx={{ color: card.color, fontSize: 16 }} />
                    </Box>
                  )}
                  
                  {!card.available && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontStyle: 'italic',
                        fontSize: '0.875rem',
                      }}
                    >
                      Coming soon
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* Quick Actions Section */}
      <Box sx={{ mt: 6 }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 3,
          }}
        >
          Quick Actions
        </Typography>
        
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
            gap: 2 
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            startIcon={<RegionsIcon />}
            onClick={() => navigate('/admin/regions/create')}
            sx={{ py: 1.5, justifyContent: 'flex-start' }}
          >
            Add New Region
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ProducerIcon />}
            onClick={() => navigate('/admin/producers/create')}
            sx={{ py: 1.5, justifyContent: 'flex-start' }}
          >
            Add New Producer
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<GrapeIcon />}
            onClick={() => navigate('/admin/grapes/create')}
            sx={{ py: 1.5, justifyContent: 'flex-start' }}
          >
            Add Grape Variety
          </Button>
          {isSystemAdmin() && (
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PeopleIcon />}
              onClick={() => navigate('/admin/users/create')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              Add Admin User
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
